import el from "@cypherpotato/el";
import page from "page";
import { api } from "../../../api";
import { Button } from "../../../components/button";
import { Card, CardContent } from "../../../components/card";
import { CommandBar } from "../../../components/command-bar";
import { Dashboard, DashboardTitle } from "../../../components/dashboard";
import { Editor, EditorDescription, EditorTitle } from "../../../components/editor";
import { TextInput } from "../../../components/form";
import { Icon } from "../../../components/icon";
import { Section } from "../../../components/layout";
import { ExternalLink, Link } from "../../../components/link";
import { BlockCodeWrapper, DashboardTextHeading, Heading, MutedText, Paragraph } from "../../../components/text";
import { TimeSpanPicker } from "../../../components/time-span-picker";
import { displayFormControl } from "../../../ui/alert";
import { formatDuration } from "../../../ui/format";
import { runOperation } from "../../../ui/operation";
import { valueFrom } from "../../../util";


export default function (ctx) {

    const chatClientId = ctx.params.id;
    const chatClient = el.state.create(null);

    function updateView() {
        runOperation("Fetching chat client...", async () => {
            const response = await api.getJson(`/api/v1/web-chat-client/${chatClientId}`);
            chatClient.value = response.data;
        });
    }

    updateView();

    function handleSave() {
        const data = {
            integrationType: "Kapso",
            integrations: {
                kapsoIntegration: {
                    apiKey: valueFrom("kapsoApiKey"),
                    phoneNumberId: valueFrom("kapsoPhoneNumberId"),
                    webhookSecretKey: valueFrom("kapsoWebhookSecretKey"),
                    sessionDuration: valueFrom("kapsoSessionDuration"),
                    continuedMessagesTemplateId: valueFrom("continuedMessagesTemplateId")
                }
            }
        };

        runOperation("Saving Kapso integration details...", async () => {
            await api.putJson(`/api/v1/web-chat-client/${chatClientId}/integrations`, data);
        });
    }

    return el.state.listening([chatClient], () =>
        Dashboard(
            Section(
                Link(el.fragment(Icon("arrow-left-line"), "Return back"), null, {
                    onClick() {
                        history.go(-1);
                        page();
                    }
                })
            ),
            DashboardTitle(
                DashboardTextHeading("Kapso settings", chatClient.value?.name)
            ),
            Section(
                CommandBar(
                    Button(
                        Icon("save-line"),
                        "Save changes",
                        {
                            onClick: handleSave
                        }
                    )
                )
            ),
            Section(
                Card(
                    CardContent(
                        Paragraph(
                            "To set up a Kapso WhatsApp integration, you need a project configured in your ",
                            ExternalLink("Kapso", "https://kapso.com.br/"), " panel with the WhatsApp Business number activated."
                        ),
                        Paragraph(
                            "After that, set the URL below as the webhook endpoint in your Kapso project settings:"
                        ),
                        BlockCodeWrapper("Integration webhook",
                            window.env.apiEndpoint + "api/v1/intl/kapso/" + chatClientId + "/hook", "url"
                        ),
                        Paragraph(
                            "Read the official documentation of ", ExternalLink("Kapso", "https://kapso.com.br/"), " for more details."
                        )
                    )
                )
            ),
            Section(
                Editor(
                    {
                        id: "kapsoApiKey",
                        value: chatClient.value?.integrations.kapsoIntegration?.apiKey,
                        renderValue(value) {
                            return Button("👀");
                        },
                        manipulateValue(value, callback) {
                            displayFormControl({
                                title: this.editorTitle,
                                message: this.editorParagraphs,
                                formControlItem: TextInput(null, "sk-kapso-...", { value }),
                                onConfirm: callback,
                                validate: [
                                    [x => x.length > 3, "The API key must be at least 3 characters long."]
                                ],
                                nullButton: true
                            })
                        }
                    },
                    EditorDescription(
                        EditorTitle("API key"),
                        Paragraph("Specify the Kapso project API key used for authentication.")
                    )
                ),
                Editor(
                    {
                        id: "kapsoPhoneNumberId",
                        value: chatClient.value?.integrations.kapsoIntegration?.phoneNumberId,
                        renderValue(value) {
                            return Button(value || MutedText("Empty"));
                        },
                        manipulateValue(value, callback) {
                            displayFormControl({
                                title: this.editorTitle,
                                message: this.editorParagraphs,
                                formControlItem: TextInput(null, "5511999999999", { value }),
                                onConfirm: callback,
                                validate: [
                                    [x => x.length > 3, "The phone number ID must be at least 3 characters long."]
                                ],
                                nullButton: true
                            })
                        }
                    },
                    EditorDescription(
                        EditorTitle("Phone number ID"),
                        Paragraph("Specify the WhatsApp Business Phone Number ID registered in Kapso.")
                    )
                ),
                Editor(
                    {
                        id: "kapsoWebhookSecretKey",
                        value: chatClient.value?.integrations.kapsoIntegration?.webhookSecretKey,
                        renderValue(value) {
                            return Button(value ? "👀" : MutedText("Not set"));
                        },
                        manipulateValue(value, callback) {
                            displayFormControl({
                                title: this.editorTitle,
                                message: this.editorParagraphs,
                                formControlItem: TextInput(null, "my-secret-key", { value }),
                                onConfirm: callback,
                                validate: [],
                                nullButton: true
                            })
                        }
                    },
                    EditorDescription(
                        EditorTitle("Webhook secret key"),
                        Paragraph("Specify the secret key used to verify webhook signatures."),
                        Paragraph("This field is optional. Leave empty to skip signature verification.")
                    )
                ),
            ),
            Section(
                Editor(
                    {
                        id: "kapsoSessionDuration",
                        value: chatClient.value?.integrations.kapsoIntegration?.sessionDuration || 10800,
                        renderValue(value) {
                            return Button(formatDuration(value));
                        },
                        manipulateValue(value, callback) {
                            displayFormControl({
                                title: this.editorTitle,
                                message: this.editorParagraphs,
                                formControlItem: TimeSpanPicker({ value: value }),
                                onConfirm: callback,
                                validate: [
                                    [x => x >= 600, "The duration must be greater than or equal to 10 minutes."],
                                    [x => x <= 7776000, "The duration must be less than or equal to 90 days."],
                                ]
                            })
                        }
                    },
                    EditorDescription(
                        EditorTitle("Session duration"),
                        Paragraph("Specify the duration that a conversation has before being restarted.")
                    )
                ),
            ),
            Heading("Scheduled messages",
                Paragraph("Define the behavior for scheduled and continued messages for this conversation.")
            ),
            Section(
                Editor(
                    {
                        id: "continuedMessagesTemplateId",
                        value: chatClient.value?.integrations.kapsoIntegration?.continuedMessagesTemplateId,
                        renderValue(value) {
                            return Button(value || MutedText("Not set"));
                        },
                        manipulateValue(value, callback) {
                            displayFormControl({
                                title: this.editorTitle,
                                message: this.editorParagraphs,
                                formControlItem: TextInput(null, "17019857127591...", { value }),
                                onConfirm: callback,
                                validate: [],
                                nullButton: true
                            })
                        }
                    },
                    EditorDescription(
                        EditorTitle("Continued messages template ID"),
                        Paragraph("Specify the Meta template ID to use for scheduled continuation messages."),
                        Paragraph("Leave empty to send continued messages without a template.")
                    )
                ),
            )
        )
    )
}
